<script setup>
  import { useVirtualList } from '@vueuse/core';
  import CarouselContainer from '~/components/carousel/CarouselContainer.vue';
  import TrendsReportCarouselCard from '~/components/trendsreport/TrendsReportCarouselCard.vue';
  import { computed } from 'vue';

  const props = defineProps({
    listName: {
      type: String,
      required: true,
    },
    articleList: {
      type: Array,
      required: true,
    },
    businessPhase: {
      type: String,
      required: false,
      default: '',
    },
    searchTerm: {
      type: String,
      required: false,
      default: '',
    },
    fromIndex: {
      type: Boolean,
      required: false,
      default: true,
    },
    itemType: {
      type: String,
      required: true,
      validator: (value) => {
        return [
          'article',
          'qa',
          'trendstalk',
          'announcement',
          'academy',
        ].includes(value);
      },
    },
    parentSlug: {
      type: String,
      required: true,
    },
    isFiltered: Boolean,
  });

  const filteredArticleList = computed(() => {
    if (!props.isFiltered) return props.articleList;

    let filteredArticles = props.articleList;

    if (props.businessPhase && props.businessPhase !== '') {
      filteredArticles = filteredArticles.filter(
        (article) => article.phase === props.businessPhase
      );
    }

    if (!props.searchTerm) return filteredArticles;

    let terms = props.searchTerm
      .split(' ')
      .filter((term) => term.trim() !== '');
    let termsLength = terms.length;
    let matches = 0;
    if (!terms.length) return filteredArticles;

    return filteredArticles.filter((article) => {
      matches = 0;
      terms.forEach((term) => {
        if (
          article.title.toLowerCase().includes(term.toLowerCase()) ||
          (article.description &&
            article.description.toLowerCase().includes(term.toLowerCase()))
        ) {
          matches++;
        }
      });
      if (matches === termsLength) return article;
    });
  });

  const screenWidth = computed(() => window.innerWidth);

  const emit = defineEmits([
    'toggle-alert',
    'toggle-favorite',
    'item-selected',
  ]);

  const { list, containerProps, wrapperProps } = useVirtualList(
    filteredArticleList,
    {
      itemWidth: () => {
        const isScreenMedium =
          screenWidth.value >= 768 && screenWidth.value < 1200;
        const isScreenWide = screenWidth.value >= 1200;

        if (props.itemType === 'trendstalk') {
          if (isScreenMedium) return 276;
          if (isScreenWide) return 399;
          return 222;
        } else if (props.itemType === 'academy') {
          if (isScreenMedium) return 276;
          if (isScreenWide) return 401;
          return 222;
        } else if (props.itemType === 'announcement') {
          return isScreenWide ? 490 : 300;
        }
        return isScreenWide ? 373 : 300;
      },
    }
  );
</script>

<template>
  <CarouselContainer
    :id="`trendsreport-carousel-${props.listName}`"
    class="trendsreport-carousel-container"
    :container-props="containerProps"
    :wrapper-props="wrapperProps"
    :class="props.itemType === 'article' ? 'article' : ''"
  >
    <TrendsReportCarouselCard
      v-for="article in list"
      :key="`${props.listName}-${article.data.id}`"
      :item="article.data"
      :item-type="itemType"
      :parent-slug="parentSlug"
      :from-index="fromIndex"
      @toggle-alert="emit('toggle-alert', $event)"
      @toggle-favorite="emit('toggle-favorite', $event)"
      @item-selected="emit('item-selected', $event, parentSlug)"
    />
  </CarouselContainer>
</template>

<style lang="scss" scoped>
  .trendsreport-carousel-container {
    margin-bottom: 4rem;
    min-height: 150px;
  }

  :deep(.carousel-container__inner) {
    gap: 3rem;
    // padding-bottom: 16px;
  }
  .article {
    :deep(.carousel-container__inner) {
      padding-bottom: 16px;
    }
  }
</style>
